/**
 * Ideas Generation View
 *
 * Two-phase word list generation:
 * 1. Generate lightweight ideas with sample words
 * 2. Review ideas and generate full word lists one-by-one
 */

import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useInitialData } from '../../../contexts/InitialDataContext';
import { useGenerateIdeas } from '../services/wordListsApi';
import { WordsNavigation } from '../components/WordsNavigation';
import type { WordListIdea, WordListCategoryType } from '../types/generation.types';

interface LocationState {
  ideas: WordListIdea[];
  categoryType: WordListCategoryType;
  languageCode: string;
}

const CATEGORY_OPTIONS: { value: WordListCategoryType; label: string; description: string }[] = [
  {
    value: 'topics',
    label: 'Topics',
    description: 'Practical vocabulary organized by themes (Business, Travel, Food)',
  },
  {
    value: 'exams',
    label: 'Exams',
    description: 'Vocabulary for standardized tests (IELTS, TOEFL, Cambridge)',
  },
  {
    value: 'vocabulary',
    label: 'Vocabulary',
    description: 'Graded word lists by CEFR level (A1-C2)',
  },
];

const DIFFICULTY_OPTIONS = [
  'Beginner',
  'Elementary',
  'Intermediate',
  'Upper-Intermediate',
  'Advanced',
] as const;

export function IdeasReviewView() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationState | null;

  // Form state for generating new ideas
  const [categoryType, setCategoryType] = useState<WordListCategoryType>(
    state?.categoryType ?? 'topics'
  );
  const [prompt, setPrompt] = useState('');
  const [ideaCount, setIdeaCount] = useState(5);

  // Ideas state
  const [ideas, setIdeas] = useState<WordListIdea[]>(state?.ideas ?? []);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const { state: initialDataState } = useInitialData();
  const languages = (initialDataState.data?.languages ?? []).filter(
    (lang) => lang.code !== 'zxx' && lang.code !== 'other'
  );
  const learningGoals = initialDataState.data?.learningGoals ?? [];

  const generateIdeasMutation = useGenerateIdeas();

  // Get language from URL params or state
  const languageCode =
    searchParams.get('lang') || state?.languageCode || languages[0]?.code || 'en';
  const languageName = languages.find((l) => l.code === languageCode)?.name || languageCode;

  const handleGenerateIdeas = async () => {
    try {
      const newIdeas = await generateIdeasMutation.mutateAsync({
        type: categoryType,
        language_code: languageCode,
        prompt: prompt.trim() || undefined,
        count: ideaCount,
      });

      setIdeas(newIdeas);
      toast.success(`Generated ${newIdeas.length} word list ideas`);
    } catch (error) {
      toast.error(`Failed to generate ideas: ${(error as Error).message}`);
    }
  };

  const handleGenerateList = (index: number) => {
    const idea = ideas[index];

    // Navigate to Generate List page with idea data pre-filled
    navigate(`/words/lists/generate?lang=${languageCode}`, {
      state: {
        idea,
        categoryType,
      },
    });
  };

  const updateIdea = (index: number, updates: Partial<WordListIdea>) => {
    setIdeas((prev) => prev.map((idea, i) => (i === index ? { ...idea, ...updates } : idea)));
  };

  const removeIdea = (index: number) => {
    setIdeas((prev) => prev.filter((_, i) => i !== index));
  };

  const getLearningGoalEmoji = (goalId: number) => {
    const goal = learningGoals.find((g) => g.id === goalId);
    return goal?.icon_emoji || '📚';
  };

  const isGeneratingIdeas = generateIdeasMutation.isPending;

  return (
    <div className="p-6">
      <WordsNavigation />

      <h1 className="text-2xl font-bold text-gray-900 mb-6">Generate Word List Ideas</h1>

      {/* Generation Form */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Category Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category Type</label>
            <div className="space-y-2">
              {CATEGORY_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    categoryType === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="categoryType"
                    value={option.value}
                    checked={categoryType === option.value}
                    onChange={() => setCategoryType(option.value)}
                    className="mt-1 mr-3"
                    disabled={isGeneratingIdeas}
                  />
                  <div>
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Prompt and Options */}
          <div className="md:col-span-2 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Prompt (Optional)
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                placeholder="Add specific requirements or constraints..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isGeneratingIdeas}
              />
            </div>

            <div className="flex items-end gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Ideas
                </label>
                <select
                  value={ideaCount}
                  onChange={(e) => setIdeaCount(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isGeneratingIdeas}
                >
                  {[3, 5, 10, 15, 20].map((count) => (
                    <option key={count} value={count}>
                      {count} ideas
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-sm text-gray-500">
                Language: <span className="font-medium">{languageName}</span>
              </div>

              <button
                onClick={handleGenerateIdeas}
                disabled={isGeneratingIdeas}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingIdeas ? 'Generating...' : 'Generate Ideas'}
              </button>
            </div>
          </div>
        </div>

        {/* Loading indicator */}
        {isGeneratingIdeas && (
          <div className="mt-4 flex items-center gap-3 text-gray-600">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <span>Generating {ideaCount} word list ideas...</span>
          </div>
        )}
      </div>

      {/* Ideas Table */}
      {ideas.length > 0 && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Generated Ideas ({ideas.length})
            </h2>
            <p className="text-sm text-gray-500">
              Click "Generate" on an idea to create the full word list
            </p>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name & Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Difficulty
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Goals
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sample Words
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Words
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ideas.map((idea, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {/* Name & Description */}
                    <td className="px-4 py-4">
                      {editingIndex === index ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={idea.name}
                            onChange={(e) => updateIdea(index, { name: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <textarea
                            value={idea.description}
                            onChange={(e) => updateIdea(index, { description: e.target.value })}
                            rows={2}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      ) : (
                        <div>
                          <div className="font-medium text-gray-900">{idea.name}</div>
                          <div className="text-sm text-gray-500 line-clamp-2">
                            {idea.description}
                          </div>
                        </div>
                      )}
                    </td>

                    {/* Difficulty */}
                    <td className="px-4 py-4">
                      {editingIndex === index ? (
                        <select
                          value={idea.difficulty}
                          onChange={(e) =>
                            updateIdea(index, {
                              difficulty: e.target.value as WordListIdea['difficulty'],
                            })
                          }
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          {DIFFICULTY_OPTIONS.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          {idea.difficulty}
                        </span>
                      )}
                    </td>

                    {/* Learning Goals */}
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {idea.learningGoalIds.length > 0 ? (
                          idea.learningGoalIds.map((goalId) => (
                            <span key={goalId} className="text-lg" title={String(goalId)}>
                              {getLearningGoalEmoji(goalId)}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">None</span>
                        )}
                      </div>
                    </td>

                    {/* Sample Words */}
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {idea.sampleWords.slice(0, 5).map((word, i) => (
                          <span
                            key={i}
                            className="inline-flex px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded"
                          >
                            {word}
                          </span>
                        ))}
                      </div>
                    </td>

                    {/* Estimated Word Count */}
                    <td className="px-4 py-4 text-center">
                      {editingIndex === index ? (
                        <input
                          type="number"
                          value={idea.estimatedWordCount}
                          onChange={(e) =>
                            updateIdea(index, { estimatedWordCount: Number(e.target.value) })
                          }
                          min={10}
                          max={200}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                        />
                      ) : (
                        <span className="text-gray-900">{idea.estimatedWordCount}</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {editingIndex === index ? (
                          <button
                            onClick={() => setEditingIndex(null)}
                            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                          >
                            Done
                          </button>
                        ) : (
                          <button
                            onClick={() => setEditingIndex(index)}
                            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          onClick={() => removeIdea(index)}
                          className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                        <button
                          onClick={() => handleGenerateList(index)}
                          className="px-4 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Generate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
